import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

import { useAppDispatch, useAppSelector } from "store/index";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import { BannerItem } from "store/cms/banners/slice";
import { CollectionItem } from "store/gameCenter/collections/slice";
import { TagItem } from "store/gameCenter/tags/slice";

import { usePermissions } from "helpers/usePermission";

import RoundBtn from "components/buttons/RoundBtn";

import MainPageDragItem from "./components/MainPageDragItem";
import MainPageSortItem from "./components/MainPageSortItem";
import { selectFilteredGeo } from "store/geoFilter/selectors";

import "./Widgets.scss";
import WidgetsListSkeleton from "components/global/Skeleton/WidgetsListSkeleton/WidgetsListSkeleton";
import { detectBrandKey } from "../../../configs/brands";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const ITEM_TYPE = {
  COLLECTION: "collection",
  TAG: "tag",
  PAGE_ITEM: "page_item",
  BANNERS: "banner",
  GAMES: "game",
  TOKEN: "token",
  CUSTOM_WIDGET: "customWidget",
};

interface IPageItem {
  instanceType: string;
  instanceId: number;
  title: string;
  priorityOrder: number;
  image: string;
}

const MainPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activePermissions = usePermissions("cms_widgets_main_page");

  const geoList = useAppSelector(selectFilteredGeo);

  const [activeGeo, setActiveGeo] = useState<string>();
  const [collections, setCollections] = useState<
    { value: number; label: string; img: string }[]
  >([]);
  const [tags, setTags] = useState<
    { value: number; label: string; img: string }[]
  >([]);
  const [banners, setBanners] = useState<
    { value: number; label: string; img: string; geos: string[] }[]
  >([]);
  const [games, setGames] = useState<
    { value: number; label: string; img: string; geos: string[] }[]
  >([]);
  const [tokens, setTokens] = useState<
    { value: number; label: string; img: string; type: string }[]
  >([]);
  const [customWidgets, setCustomWidgets] = useState<
    { value: number; label: string; img: string; geos: string[] }[]
  >([]);
  const [pageItems, setPageItems] = useState<
    {
      value: number;
      label: string;
      type: string;
      priority: number;
      img?: string;
    }[]
  >([]);
  const [pageItemsTags, setPageItemsTags] = useState<number[]>([]);
  const [pageItemsCollection, setPageItemsCollection] = useState<number[]>([]);
  const [pageItemsBanners, setPageItemsBanners] = useState<number[]>([]);
  const [pageItemsGames, setPageItemsGames] = useState<number[]>([]);
  const [pageItemsTokens, setPageItemsTokens] = useState<number[]>([]);
  const [pageItemsCustomWidgets, setPageItemsCustomWidgets] = useState<
    number[]
  >([]);
  const [itemsFetched, setItemsFetched] = useState(false);
  const [selectedItems, setSelectedItems] = useState<
    { value: number; label: string; type: string; priority: number }[]
  >([]);
  const [selectedItemsToDelete, setSelectedItemsToDelete] = useState<
    { value: number; label: string; type: string; priority: number }[]
  >([]);

  const isTokenAvailable = ["pumpkings", "nextwin"].includes(detectBrandKey());

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (!activePermissions?.includes("read") && activePermissions !== null) {
      navigate("/dashboard");
    }
  }, []);

  useEffect(() => {
    if (geoList?.length) {
      setActiveGeo(geoList[0]);
    }
  }, [geoList]);

  const getPageItems = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.mainPageList}?geo=${activeGeo}`,
      );
      setPageItems(
        result.data.items.map((el: IPageItem) => ({
          value: el.instanceId,
          type: el.instanceType,
          label: el.title,
          priority: el.priorityOrder,
          showType: true,
        })),
      );
      setPageItemsCollection(
        result.data.items
          .filter((el: IPageItem) => el.instanceType === "collection")
          .map((item: IPageItem) => item.instanceId),
      );
      setPageItemsTags(
        result.data.items
          .filter((el: IPageItem) => el.instanceType === "tag")
          .map((item: IPageItem) => item.instanceId),
      );
      setPageItemsBanners(
        result.data.items
          .filter((el: IPageItem) => el.instanceType === "banner")
          .map((item: IPageItem) => item.instanceId),
      );
      setPageItemsGames(
        result.data.items
          .filter((el: IPageItem) => el.instanceType === "game")
          .map((item: IPageItem) => item.instanceId),
      );
      setPageItemsCustomWidgets(
        result.data.items
          .filter((el: IPageItem) => el.instanceType === "customWidget")
          .map((item: IPageItem) => item.instanceId),
      );
      if (isTokenAvailable) {
        setPageItemsTokens(
          result.data.items
            .filter((el: IPageItem) => el.instanceType === "token")
            .map((item: IPageItem) => item.instanceId),
        );
      }
    } catch (error) {
      console.error(error);
    }
    setItemsFetched(true);
  };

  useEffect(() => {
    if (activeGeo) {
      setItemsFetched(false);
      getPageItems();
    }
  }, [activeGeo]);

  const getBanners = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.bannersList}?limit=500&offset=0`,
      );

      setBanners(
        result.data.items
          .filter((banner: BannerItem) => banner.isActive)
          .map((el: BannerItem) => {
            return {
              img: el.image,
              value: el.id,
              label: el.title,
              geos: el.geo,
              type: ITEM_TYPE.BANNERS,
            };
          }),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getCollections = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.collectionsList}?limit=500&offset=0`,
      );
      setCollections(
        result.data.items.map((el: CollectionItem) => ({
          value: el.id,
          label: el.title,
          img: el.image,
          type: ITEM_TYPE.COLLECTION,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getTags = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.tagsList}?limit=500&offset=0`,
      );
      setTags(
        result.data.items.map((el: TagItem) => ({
          value: el.id,
          label: el.title,
          img: el.image,
          type: ITEM_TYPE.TAG,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getGames = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.gamesWidgetList}?limit=500&offset=0`,
      );
      setGames(
        result.data.items.map((el: TagItem) => ({
          value: el.id,
          label: el.title,
          img: el.image,
          type: ITEM_TYPE.GAMES,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getTokens = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.tokenWidgetList}?limit=500&offset=0`,
      );

      setTokens(
        result.data.items
          .filter((item: any) => item.isActive)
          .map((el: any) => ({
            value: el.id,
            label: el.title,
            img: el.image || el.imageCdn,
            type: ITEM_TYPE.TOKEN,
          })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getCustomWidgets = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.customizedList}?limit=500&offset=0&isActive=1&geo=${activeGeo}`,
      );

      setCustomWidgets(
        result.data.items.map((el: any) => ({
          value: el.id,
          label: el.title,
          img: "",
          geos: el.geo,
          type: ITEM_TYPE.CUSTOM_WIDGET,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!activeGeo) {
      return;
    }

    getTags();
    getCollections();
    getBanners();
    getGames();
    getCustomWidgets();
    if (isTokenAvailable) {
      getTokens();
    }
  }, [activeGeo]);

  const handleUpdateItems = async (items: any[]) => {
    try {
      await instance.post(`${apiRoutes.mainPageUpdate}`, {
        geo: activeGeo,
        items: items.map((el, idx) => {
          return {
            instanceType: el.type,
            instanceId: el.value,
            priorityOrder: idx + 1,
          };
        }),
      });
      setPageItems([]);
      setPageItemsCollection([]);
      setPageItemsTags([]);
      setItemsFetched(false);
      getPageItems();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("itemsUpdated"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("itemsUpdateError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const moveItem = async (dragIndex: number, hoverIndex: number) => {
    let items = Array.from(pageItems);
    const [reorderedItem] = items.splice(dragIndex, 1);
    items.splice(hoverIndex, 0, reorderedItem);
    setPageItems(items);
    setItemsFetched(false);
    try {
      await instance.post(`${apiRoutes.mainPageUpdate}`, {
        geo: activeGeo,
        items: items.map((el, idx) => {
          return {
            instanceType: el.type,
            instanceId: el.value,
            priorityOrder: idx + 1,
          };
        }),
      });
      setPageItems([]);
      setPageItemsCollection([]);
      setPageItemsTags([]);
      setItemsFetched(false);
      getPageItems();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("itemsOrderUpdated"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("itemsOrderUpdateError"),
          type: NotificationTypes.error,
        }),
      );
    }
    setItemsFetched(true);
  };

  const imageMap = useMemo<
    Record<string, { value: number; img: string; label: string }[]>
  >(
    () => ({
      [ITEM_TYPE.BANNERS]: banners,
      [ITEM_TYPE.COLLECTION]: collections,
      [ITEM_TYPE.TAG]: tags,
      [ITEM_TYPE.GAMES]: games,
      [ITEM_TYPE.TOKEN]: tokens,
      [ITEM_TYPE.CUSTOM_WIDGET]: customWidgets,
    }),
    [banners, collections, tags, games, tokens, customWidgets],
  );

  const getImageByType = (type: string, id: number) => {
    return imageMap[type]?.find((i) => i.value === id)?.img;
  };
  const getLabelByType = (type: string, id: number) => {
    return imageMap[type]?.find((i) => i.value === id)?.label;
  };

  return (
    <div className="main-page-wrapper">
      <div className="main-page-content">
        <div className="main-page-widget">
          <div className="main-page-widget--box">
            <div className="main-page-widget--tabs">
              <div className="game-modal-tabs small">
                {geoList.map((el) => (
                  <div
                    className={`tab-item ${activeGeo === el ? "active" : ""}`}
                    key={el}
                    onClick={() => {
                      setActiveGeo(el);
                      setPageItems([]);
                      setPageItemsCollection([]);
                      setPageItemsTags([]);
                    }}
                  >
                    {el.toLowerCase() === "ww" ? (
                      <span className="mr-5">🌎</span>
                    ) : (
                      <img
                        src={`https://flagcdn.com/w40/${el.toLowerCase()}.png`}
                        alt=""
                      />
                    )}
                    {t(el)}
                  </div>
                ))}
              </div>
            </div>
            {itemsFetched ? (
              <div className="main-page-widget--list">
                {pageItems.map((el, index) => (
                  <MainPageSortItem
                    isMainWidgetPage
                    handleClick={() => {
                      if (!permissions?.delete) return;

                      if (selectedItemsToDelete.includes(el)) {
                        setSelectedItemsToDelete(
                          selectedItemsToDelete.filter((item) => item !== el),
                        );
                      } else {
                        setSelectedItemsToDelete([
                          ...selectedItemsToDelete,
                          el,
                        ]);
                      }
                    }}
                    isSelected={selectedItemsToDelete.includes(el)}
                    key={`${el.value}-${el.type}`}
                    item={{
                      ...el,
                      img: getImageByType(el.type, el.value),
                      label: el.label || getLabelByType(el.type, el.value),
                    }}
                    index={index}
                    moveItem={moveItem}
                    isDraggable={permissions?.update}
                    readOnly={!permissions?.update}
                  />
                ))}
              </div>
            ) : (
              <WidgetsListSkeleton />
            )}
            <div className="main-page-btns">
              {permissions?.create && (
                <RoundBtn
                  handleClick={() => {
                    setItemsFetched(false);
                    const newArray = [...pageItems, ...selectedItems];
                    handleUpdateItems(newArray);
                    setSelectedItems([]);
                    setPageItems(newArray);
                  }}
                  txt="+"
                  disabled={selectedItems.length === 0}
                />
              )}
              {permissions?.delete && (
                <RoundBtn
                  handleClick={() => {
                    const newArray = pageItems.filter(
                      (el) => !selectedItemsToDelete.includes(el),
                    );
                    handleUpdateItems(newArray);
                    setSelectedItemsToDelete([]);
                    setPageItems(newArray);
                  }}
                  txt="-"
                  disabled={selectedItemsToDelete.length === 0}
                />
              )}
            </div>
          </div>
        </div>
        <div className="main-page-lists">
          <div className="main-page-list-box">
            <div className="main-page-title collections">
              {t("collections")}
            </div>
            <div className="main-page-list-content">
              {itemsFetched ? (
                <>
                  {collections
                    .filter((item) => !pageItemsCollection.includes(item.value))
                    .map((el: any) => (
                      <MainPageDragItem
                        handleClick={() => {
                          if (!permissions?.create) return;

                          if (selectedItems.includes(el)) {
                            setSelectedItems(
                              selectedItems.filter((item) => item !== el),
                            );
                          } else {
                            setSelectedItems([...selectedItems, el]);
                          }
                        }}
                        isSelected={selectedItems.includes(el)}
                        key={el.value}
                        item={el}
                        type={ITEM_TYPE.COLLECTION}
                      />
                    ))}
                </>
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>
          </div>
          <div className="main-page-list-box">
            <div className="main-page-title tags">{t("tags")}</div>
            <div className="main-page-list-content">
              {itemsFetched ? (
                <>
                  {tags
                    .filter((item) => !pageItemsTags.includes(item.value))
                    .map((el: any) => (
                      <MainPageDragItem
                        handleClick={() => {
                          if (selectedItems.includes(el)) {
                            setSelectedItems(
                              selectedItems.filter((item) => item !== el),
                            );
                          } else {
                            setSelectedItems([...selectedItems, el]);
                          }
                        }}
                        isSelected={selectedItems.includes(el)}
                        key={el.value}
                        item={el}
                        type={ITEM_TYPE.TAG}
                      />
                    ))}
                </>
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>
          </div>
          <div className="main-page-list-box">
            <div className="main-page-title banners">{t("banners")}</div>
            <div className="main-page-list-content">
              {itemsFetched ? (
                <>
                  {banners
                    .filter(
                      (item) =>
                        !pageItemsBanners.includes(item.value) &&
                        activeGeo &&
                        item.geos?.includes(activeGeo),
                    )
                    .map((el: any) => (
                      <MainPageDragItem
                        handleClick={() => {
                          if (selectedItems.includes(el)) {
                            setSelectedItems(
                              selectedItems.filter((item) => item !== el),
                            );
                          } else {
                            setSelectedItems([...selectedItems, el]);
                          }
                        }}
                        isSelected={selectedItems.includes(el)}
                        key={el.value}
                        item={el}
                        type={ITEM_TYPE.BANNERS}
                      />
                    ))}
                </>
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>
          </div>
          <div className="main-page-list-box">
            <div className="main-page-title games">{t("games")}</div>
            <div className="main-page-list-content">
              {itemsFetched ? (
                <>
                  {games
                    .filter((item) => !pageItemsGames.includes(item.value))
                    .map((el: any) => (
                      <MainPageDragItem
                        handleClick={() => {
                          if (selectedItems.includes(el)) {
                            setSelectedItems(
                              selectedItems.filter((item) => item !== el),
                            );
                          } else {
                            setSelectedItems([...selectedItems, el]);
                          }
                        }}
                        isSelected={selectedItems.includes(el)}
                        key={el.value}
                        item={el}
                        type={ITEM_TYPE.GAMES}
                      />
                    ))}
                </>
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>
          </div>
          <div className="main-page-list-box">
            <div className="main-page-title custom-widgets">
              {t("customized")}
            </div>

            <div className="main-page-list-content">
              {itemsFetched ? (
                <>
                  {customWidgets
                    .filter(
                      (item) => !pageItemsCustomWidgets.includes(item.value),
                    )
                    .map((el: any) => (
                      <MainPageDragItem
                        handleClick={() => {
                          if (selectedItems.includes(el)) {
                            setSelectedItems(
                              selectedItems.filter((item) => item !== el),
                            );
                          } else {
                            setSelectedItems([...selectedItems, el]);
                          }
                        }}
                        isSelected={selectedItems.includes(el)}
                        key={el.value}
                        item={el}
                        type={ITEM_TYPE.CUSTOM_WIDGET}
                      />
                    ))}
                </>
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>
          </div>
          {isTokenAvailable && (
            <div className="main-page-list-box">
              <div className="main-page-title tokens">{t("tokens")}</div>
              <div className="main-page-list-content">
                {itemsFetched ? (
                  <>
                    {tokens
                      .filter((item) => !pageItemsTokens.includes(item.value))
                      .map((el: any) => (
                        <MainPageDragItem
                          handleClick={() => {
                            if (selectedItems.includes(el)) {
                              setSelectedItems(
                                selectedItems.filter((item) => item !== el),
                              );
                            } else {
                              setSelectedItems([...selectedItems, el]);
                            }
                          }}
                          isSelected={selectedItems.includes(el)}
                          key={el.value}
                          item={el}
                          type={ITEM_TYPE.TOKEN}
                        />
                      ))}
                  </>
                ) : (
                  <WidgetsListSkeleton />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MainPage;
